import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, PageBody, Field } from "@/components/PageChrome";
import { Input, Button } from "@/components/controls";
import { Plus, Trash2, Save, RefreshCw, Mail } from "lucide-react";
import { T } from "@/lib/testIds";

const GLOBAL_LABELS = {
  rmb_conversion_rate: "RMB Conversion Rate",
  international_freight_electrical: "Intl Freight – Electrical ₹",
  international_freight_non_electrical: "Intl Freight – Non-Electrical ₹",
  aggregator_commission_percentage: "Aggregator Commission %",
  importrend_commission_percentage: "Importrend Margin %",
  reseller_commission_percentage: "Reseller Margin %",
  gst_percent: "GST %",
};

export default function Settings() {
  const [globals, setGlobals] = useState({});
  const [dropdowns, setDropdowns] = useState({});
  const [customFields, setCustomFields] = useState({});
  const [calculations, setCalculations] = useState([]);
  const [newDD, setNewDD] = useState({});
  const [newCF, setNewCF] = useState({});
  const [tab, setTab] = useState("globals");
  const [brackets, setBrackets] = useState([]);
  // Per-Account Overrides tab state (Feb 25 2026)
  const [accounts, setAccounts] = useState([]);
  const [accountOverrides, setAccountOverrides] = useState({}); // { account_id -> row }
  // Per-account FORMULA editor state — opens as a modal on click
  const [formulaEditor, setFormulaEditor] = useState(null); // { account_id, defaults, overrides }
  // Per-account STANDALONE STATUS RULES editor — opens as a modal on click
  const [rulesEditor, setRulesEditor] = useState(null); // { account_id, rules: [] }

  const loadAccountOverrides = async () => {
    try {
      const [acctRes, ovRes] = await Promise.all([
        api.get("/accounts"),
        api.get("/settings/account-globals"),
      ]);
      setAccounts(acctRes.data || []);
      const byId = {};
      for (const o of ovRes.data || []) byId[o.account_id] = o;
      setAccountOverrides(byId);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { if (tab === "accounts") loadAccountOverrides(); }, [tab]);

  const setAcctOverrideField = (aid, key, val) => {
    setAccountOverrides((prev) => ({
      ...prev,
      [aid]: { ...(prev[aid] || { account_id: aid }), [key]: val === "" ? null : Number(val) },
    }));
  };

  const persistAcctOverride = async (aid) => {
    const row = accountOverrides[aid] || {};
    const payload = {};
    for (const k of ["rmb_conversion_rate", "international_freight_electrical",
                     "international_freight_non_electrical", "aggregator_commission_percentage",
                     "importrend_commission_percentage", "gst_percent"]) {
      payload[k] = row[k] ?? null;
    }
    try {
      const { data } = await api.patch(`/settings/account-globals/${encodeURIComponent(aid)}`, payload);
      setAccountOverrides((prev) => ({ ...prev, [aid]: { ...prev[aid], ...(data.after || {}) } }));
      toast.success(`Saved overrides for ${aid}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearAcctOverride = async (aid) => {
    if (!window.confirm(`Clear all per-account overrides for ${aid}? Values will fall back to Global Values.`)) return;
    try {
      await api.delete(`/settings/account-globals/${encodeURIComponent(aid)}`);
      setAccountOverrides((prev) => { const next = { ...prev }; delete next[aid]; return next; });
      toast.success(`Cleared ${aid}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openFormulaEditor = async (aid) => {
    try {
      const { data } = await api.get(`/settings/account-globals/${encodeURIComponent(aid)}/formulas`);
      setFormulaEditor({ account_id: aid, defaults: data.defaults || {},
                          overrides: { ...(data.overrides || {}) } });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setFormulaOverride = (key, value) => {
    setFormulaEditor((s) => ({ ...s, overrides: { ...(s.overrides || {}), [key]: value } }));
  };

  const persistFormulaOverride = async (key) => {
    if (!formulaEditor) return;
    const raw = formulaEditor.overrides[key];
    const value = raw === "" || raw == null ? null : raw;
    try {
      const { data } = await api.patch(
        `/settings/account-globals/${encodeURIComponent(formulaEditor.account_id)}/formulas/${encodeURIComponent(key)}`,
        { formula: value },
      );
      setFormulaEditor((s) => ({ ...s, overrides: data.overrides || {} }));
      toast.success(value ? `Saved override for ${key}` : `Cleared ${key} override`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Standalone status rules editor — fetch/save/edit per account.
  // Rules are stored as an ordered list; the first matching rule wins.
  // When zero rules are configured on an account, standalone procurement
  // entries created for it default to china_order_status = "Blank".
  const RULE_FIELD_CHOICES = [
    "china_transfer_price", "china_landing_price", "china_costing",
    "china_price", "china_shipping", "china_weight_gram",
    "china_qty_to_order", "moq",
    "china_margin", "china_margin_gst", "china_gst",
    "china_aggregator_margin", "china_international_freight_charges",
  ];
  const RULE_OPERATORS = [">", "<", ">=", "<=", "=="];
  const RULE_STATUS_CHOICES = ["Eligible", "OTS", "Added", "Dispatched", "Delivered", "Blank"];

  const openRulesEditor = async (aid) => {
    try {
      const { data } = await api.get(`/settings/standalone-status-rules/${encodeURIComponent(aid)}`);
      setRulesEditor({ account_id: aid, rules: [...(data.rules || [])] });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRuleAt = (idx, patch) => {
    setRulesEditor((s) => ({
      ...s,
      rules: s.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
  };
  const addRule = () => {
    setRulesEditor((s) => ({
      ...s,
      rules: [...s.rules, { field: "china_transfer_price", operator: ">", value: 1, result_status: "Eligible" }],
    }));
  };
  const removeRuleAt = (idx) => {
    setRulesEditor((s) => ({ ...s, rules: s.rules.filter((_, i) => i !== idx) }));
  };
  const moveRule = (idx, dir) => {
    setRulesEditor((s) => {
      const rules = [...s.rules];
      const j = idx + dir;
      if (j < 0 || j >= rules.length) return s;
      [rules[idx], rules[j]] = [rules[j], rules[idx]];
      return { ...s, rules };
    });
  };
  const persistRules = async () => {
    if (!rulesEditor) return;
    // Coerce numeric-looking values so > / < compare correctly on the server.
    const payload = rulesEditor.rules.map((r) => {
      const v = r.value;
      const num = typeof v === "string" && v.trim() !== "" && !isNaN(Number(v)) ? Number(v) : v;
      return { field: r.field, operator: r.operator, value: num, result_status: r.result_status };
    });
    try {
      const { data } = await api.put(
        `/settings/standalone-status-rules/${encodeURIComponent(rulesEditor.account_id)}`,
        { rules: payload },
      );
      setRulesEditor((s) => ({ ...s, rules: data.rules || [] }));
      toast.success(`Saved ${data.rules?.length || 0} rule(s) for ${rulesEditor.account_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    try {
      const [g, d, c, calc] = await Promise.all([
        api.get("/settings/globals"),
        api.get("/settings/dropdowns"),
        api.get("/settings/custom-fields"),
        api.get("/settings/calculations"),
      ]);
      setGlobals(g.data); setDropdowns(d.data); setCustomFields(c.data);
      setCalculations(calc.data.calculations);
      setBrackets(g.data.last_mile_brackets || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveGlobals = async () => {
    try {
      const payload = { ...globals, last_mile_brackets: brackets };
      const { data } = await api.patch("/settings/globals", payload);
      setGlobals(data);
      setBrackets(data.last_mile_brackets || []);
      toast.success("Globals & brackets updated");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addDD = async (key) => {
    const value = (newDD[key] || "").trim();
    if (!value) return;
    try { const { data } = await api.post(`/settings/dropdowns/${key}`, { value }); setDropdowns(data); setNewDD((s) => ({ ...s, [key]: "" })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeDD = async (key, value) => {
    try { const { data } = await api.delete(`/settings/dropdowns/${key}/${encodeURIComponent(value)}`); setDropdowns(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const addCF = async (module) => {
    const name = (newCF[module] || "").trim();
    if (!name) return;
    try { const { data } = await api.post(`/settings/custom-fields/${module}`, { name, type: "text" }); setCustomFields(data); setNewCF((s) => ({ ...s, [module]: "" })); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeCF = async (module, name) => {
    try { const { data } = await api.delete(`/settings/custom-fields/${module}/${encodeURIComponent(name)}`); setCustomFields(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const MODULES = ["inventory", "accounts", "orders", "mapping", "warehouse", "bank"];

  const TABS = [
    { k: "globals", label: "Global Values" },
    { k: "accounts", label: "Per-Account Overrides" },
    { k: "calculations", label: "Calculations" },
    { k: "dropdowns", label: "Dropdowns" },
    { k: "custom", label: "Custom Fields" },
    { k: "label", label: "Label Template" },
    { k: "gmail", label: "Gmail Reader" },
  ];

  // ---- Gmail settings tab (Feb 2026) ----
  const [gmailCfg, setGmailCfg] = useState({
    imap_host: "imap.gmail.com", imap_port: 993, email: "", app_password: "",
    folder: "INBOX", search_days: 3,
  });

  // In-memory patch for the Calculations tab. `setCalcOverride` updates the
  // local row immediately; `persistCalcOverride` fires the PATCH on blur so we
  // don't spam the backend on every keystroke.
  const setCalcOverride = (key, patch) => {
    setCalculations((cur) => cur.map((c) => c.key === key ? { ...c, ...patch } : c));
  };
  const persistCalcOverride = async (key, patch) => {
    try {
      await api.patch(`/settings/calculations/${key}`, patch);
      const { data } = await api.get("/settings/calculations");
      setCalculations(data.calculations);
    } catch (e) {
      // Validation errors (400) leave the local textarea showing the invalid
      // string; re-fetch so the UI snaps back to the server-side (still-valid)
      // formula and it's obvious the edit was rejected.
      toast.error(formatApiError(e));
      try {
        const { data } = await api.get("/settings/calculations");
        setCalculations(data.calculations);
      } catch { /* noop */ }
    }
  };
  const resetCalcOverride = async (key) => {
    try {
      await api.post(`/settings/calculations/${key}/reset`);
      const { data } = await api.get("/settings/calculations");
      setCalculations(data.calculations);
      toast.success("Reset to default");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [gmailStatus, setGmailStatus] = useState(null);
  const [gmailBusy, setGmailBusy] = useState(false);
  // ---- Label Template tab (Feb 26 2026) ----
  const [labelTpl, setLabelTpl] = useState(null);
  const [labelBusy, setLabelBusy] = useState(false);
  useEffect(() => {
    if (tab !== "label") return;
    api.get("/settings/label-template")
      .then((r) => setLabelTpl(r.data.template || {}))
      .catch((e) => toast.error(formatApiError(e)));
  }, [tab]);
  const saveLabelTemplate = async () => {
    if (!labelTpl) return;
    setLabelBusy(true);
    try {
      const { data } = await api.patch("/settings/label-template", labelTpl);
      setLabelTpl(data.template || labelTpl);
      toast.success("Label template saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setLabelBusy(false);
  };
  useEffect(() => {
    if (tab !== "gmail") return;
    api.get("/gmail/settings").then((r) => setGmailCfg((s) => ({ ...s, ...r.data }))).catch(() => {});
    api.get("/gmail/status").then((r) => setGmailStatus(r.data)).catch(() => {});
  }, [tab]);
  const saveGmail = async () => {
    try {
      await api.put("/gmail/settings", gmailCfg);
      toast.success("Gmail settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const syncGmail = async () => {
    setGmailBusy(true);
    try {
      const { data } = await api.post("/gmail/sync");
      if (data.queued === false) {
        toast.info(data.message || "A sync is already running");
      } else {
        toast.success(data.message || "Sync started — polling for progress");
      }
      // Start polling status every 5s until the run finishes (status != "running").
      const pollId = setInterval(async () => {
        try {
          const { data: s } = await api.get("/gmail/status");
          setGmailStatus(s);
          if (s.status && s.status !== "running") {
            clearInterval(pollId);
            setGmailBusy(false);
            if (s.status === "failed") {
              toast.error(`Gmail sync failed: ${s.error || "unknown"}`);
            } else {
              toast.success(`Sync complete — parsed ${s.new_events_parsed ?? 0} new · applied refunds:${s.refunds_applied ?? 0} payouts:${s.payouts_applied ?? 0} commissions:${s.commissions_applied ?? 0}`);
            }
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { toast.error(formatApiError(e)); setGmailBusy(false); }
  };
  const reapplyCached = async () => {
    setGmailBusy(true);
    try {
      const { data } = await api.post("/gmail/reapply");
      const bk = data.by_kind || {};
      toast.success(`Re-applied ${data.reapplied_events ?? 0} cached events · refunds:${bk.refund ?? 0} payouts:${bk.payout ?? 0} commissions:${bk.commission ?? 0} delivery:${bk.delivery ?? 0}`);
      const { data: s } = await api.get("/gmail/status");
      setGmailStatus(s);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGmailBusy(false); }
  };
  const rescanUnclassified = async () => {
    if (!window.confirm("Clear cached 'unclassified' emails so the next sync re-fetches them with the current classifier patterns? Use this after adding new patterns.")) return;
    setGmailBusy(true);
    try {
      const { data } = await api.post("/gmail/rescan-unclassified");
      toast.success(data.message || `Cleared ${data.cleared_markers ?? 0} markers — click 'Sync From Gmail Now' to re-fetch.`);
      const { data: s } = await api.get("/gmail/status");
      setGmailStatus(s);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGmailBusy(false); }
  };
  // Oct 19 2026 (ticket 285300) -- one-time repair for refunded orders that
  // were shown as Shipped / Return Awaiting by mistake. Counts first, then asks.
  const repairRefundStatuses = async () => {
    setGmailBusy(true);
    try {
      const { data: c } = await api.post("/gmail/admin/repair-refund-statuses?dry_run=true");
      const total = (c.marketplace_to_refunded ?? 0) + (c.return_status_restored ?? 0);
      if (!total) { toast.success("Nothing to repair — all refunded orders are already correct."); return; }
      if (!window.confirm(`Fix refunded orders?\n\n• ${c.marketplace_to_refunded} order(s): Marketplace Status Shipped → Refunded\n• ${c.return_status_restored} order(s): Return Status Return Awaiting → back to Return Received / RTO Received\n\nOnly these two fields change. Stock is not touched.`)) return;
      const { data } = await api.post("/gmail/admin/repair-refund-statuses?dry_run=false");
      toast.success(`Repaired ${data.marketplace_to_refunded} marketplace status(es) and ${data.return_status_restored} return status(es).`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGmailBusy(false); }
  };
  const [gmailAnalysis, setGmailAnalysis] = useState(null);
  const analyzeOverlap = async () => {
    setGmailBusy(true);
    try {
      const { data } = await api.get("/gmail/analyze");
      setGmailAnalysis(data);
      toast.success(`Analysis loaded — ${data.orders_total} orders, ${data.by_kind.payout.events_total} payout events, ${data.by_kind.refund.events_total} refund events`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGmailBusy(false); }
  };

  return (
    <>
      <PageHeader title="Settings" subtitle="Global values, calculations, dropdowns & custom fields" testId={T.moduleTitle("settings")} />
      <PageBody>
        <div className="border-b border-slate-200 mb-4 flex gap-4">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`settings-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-1 pb-2 text-[13px] border-b-2 -mb-px ${tab === t.k ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "globals" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="font-heading text-[13px] font-semibold">Global Values</h3>
              <Button variant="primary" onClick={saveGlobals} testId={T.saveBtn}>Save</Button>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              {Object.entries(GLOBAL_LABELS).map(([k, label]) => (
                <Field key={k} label={label}>
                  <Input testId={T.formInput(k)} type="number" value={globals[k] ?? ""} onChange={(e) => setGlobals((s) => ({ ...s, [k]: Number(e.target.value) }))} />
                </Field>
              ))}
              <Field label="China Lead Time (days)">
                <Input testId={T.formInput("china_lead_time_days")} type="number" value={globals.china_lead_time_days ?? ""} onChange={(e) => setGlobals((s) => ({ ...s, china_lead_time_days: Number(e.target.value) }))} />
              </Field>
              <Field label="Low-Stock Cover (days)">
                <Input testId={T.formInput("low_stock_cover_days")} type="number" value={globals.low_stock_cover_days ?? ""} onChange={(e) => setGlobals((s) => ({ ...s, low_stock_cover_days: Number(e.target.value) }))} />
              </Field>
              <Field label="Last-Mile: Incremental / 500g ₹">
                <Input testId={T.formInput("last_mile_incremental_per_500g")} type="number" value={globals.last_mile_incremental_per_500g ?? ""} onChange={(e) => setGlobals((s) => ({ ...s, last_mile_incremental_per_500g: Number(e.target.value) }))} />
              </Field>
              <Field label="Chat Edit / Delete Window (minutes)">
                <Input
                  testId={T.formInput("chat_edit_window_minutes")}
                  type="number"
                  min="0"
                  value={globals.chat_edit_window_minutes ?? ""}
                  onChange={(e) => setGlobals((s) => ({ ...s, chat_edit_window_minutes: Number(e.target.value) }))}
                />
                <div className="text-[10.5px] text-slate-500 font-mono mt-1">
                  How long after sending a chat message the sender can still edit or delete it. Set 0 to lock messages immediately.
                </div>
              </Field>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-[13px] font-semibold">Electrical Auto-Tag Keywords</h3>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[11px] text-slate-500 font-mono mb-1">
                Case-insensitive. If a product name contains ANY of these words, the order's
                Electrical/Non-Electrical field auto-sets to "Electrical". Otherwise "Non-Electrical".
                Applies on every save.
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="electrical-keywords-list">
                {(globals.electrical_keywords || []).map((kw, i) => (
                  <span key={i} className="inline-flex items-center gap-1 h-7 px-2 text-[11.5px] font-mono bg-amber-50 border border-amber-200 text-amber-800 rounded-sm" data-testid={`electrical-kw-${kw}`}>
                    {kw}
                    <button
                      onClick={() => setGlobals((s) => ({ ...s, electrical_keywords: (s.electrical_keywords || []).filter((_, j) => j !== i) }))}
                      className="text-amber-700 hover:text-red-700"
                      data-testid={`electrical-kw-rm-${kw}`}
                    >
                      <Trash2 size={11} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1 pt-1">
                <input
                  type="text"
                  data-testid="electrical-kw-input"
                  placeholder="Add keyword (e.g. speaker) then press Enter"
                  className="flex-1 h-8 px-2 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onKeyDown={(e) => {
                    if (e.key !== "Enter") return;
                    const val = e.currentTarget.value.trim();
                    if (!val) return;
                    if ((globals.electrical_keywords || []).some((k) => k.toLowerCase() === val.toLowerCase())) {
                      toast.info(`"${val}" is already in the list`);
                      e.currentTarget.value = "";
                      return;
                    }
                    setGlobals((s) => ({ ...s, electrical_keywords: [...(s.electrical_keywords || []), val] }));
                    e.currentTarget.value = "";
                  }}
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <div className="text-[10.5px] text-slate-400 font-mono">Remember to click "Save" (top right) after editing keywords.</div>
                <Button
                  data-testid="electrical-retag-btn"
                  onClick={async () => {
                    if (!window.confirm("Re-apply Electrical/Non-Electrical to EVERY existing order using the current keyword list?")) return;
                    try {
                      const { data } = await api.post("/orders/admin/retag-electrical");
                      toast.success(`Retag done — examined ${data.examined}, updated ${data.updated}, unchanged ${data.unchanged}`);
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                >
                  <RefreshCw size={13} /> Retag All Orders
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-[13px] font-semibold">Product URL Templates</h3>
            </div>
            <div className="p-4 space-y-2">
              <div className="text-[11px] text-slate-500 font-mono mb-2">
                Use <code className="bg-slate-100 px-1">{"{id}"}</code> for marketplace_identifier. Applied when an order is created.
              </div>
              {Object.entries(globals.product_url_templates || {}).map(([mp, tpl]) => (
                <div key={mp} className="flex gap-2 items-center">
                  <span className="w-24 text-[12.5px] font-mono text-slate-700">{mp}</span>
                  <Input
                    testId={`url-tpl-${mp}`}
                    value={tpl}
                    onChange={(e) => setGlobals((s) => ({ ...s, product_url_templates: { ...(s.product_url_templates || {}), [mp]: e.target.value } }))}
                    className="flex-1"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-[13px] font-semibold">Last-Mile Weight Brackets</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-2 items-end">
                <span className="field-label !mb-0">Up to weight (g)</span>
                <span className="field-label !mb-0">Cost (₹)</span>
                {brackets.map(([w, c], i) => (
                  <React.Fragment key={i}>
                    <Input testId={`bracket-w-${i}`} type="number" value={w} onChange={(e) => {
                      const copy = brackets.slice(); copy[i] = [Number(e.target.value), copy[i][1]]; setBrackets(copy);
                    }} />
                    <div className="flex gap-1">
                      <Input testId={`bracket-c-${i}`} type="number" value={c} onChange={(e) => {
                        const copy = brackets.slice(); copy[i] = [copy[i][0], Number(e.target.value)]; setBrackets(copy);
                      }} />
                      <button onClick={() => setBrackets(brackets.filter((_, j) => j !== i))} className="text-red-700 hover:underline text-[11.5px]"><Trash2 size={13} /></button>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              <Button className="mt-2" onClick={() => setBrackets([...brackets, [brackets.length ? brackets[brackets.length - 1][0] + 500 : 500, 80]])}>
                <Plus size={12} /> Add Bracket
              </Button>
              <div className="text-[11px] text-slate-500 font-mono mt-2">Beyond top bracket, adds "incremental / 500g" per extra 500g.</div>
            </div>
          </div>
        </div>
        )}

        {tab === "accounts" && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="font-heading text-[13px] font-semibold">Per-Account Overrides</h3>
              <div className="text-[11px] text-slate-500 font-mono">Blank cells fall back to Global Values on the left tab.</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[11px] font-mono" data-testid="acct-overrides-table">
                <thead>
                  <tr className="text-left text-slate-500 uppercase tracking-wider border-b border-slate-200 bg-slate-50">
                    <th className="py-1.5 px-2 sticky left-0 bg-slate-50">Account ID</th>
                    <th className="py-1.5 px-2">RMB Conv</th>
                    <th className="py-1.5 px-2">Freight Elec</th>
                    <th className="py-1.5 px-2">Freight Non-Elec</th>
                    <th className="py-1.5 px-2">Aggregator %</th>
                    <th className="py-1.5 px-2">Importrend %</th>
                    <th className="py-1.5 px-2">GST %</th>
                    <th className="py-1.5 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => {
                    const aid = a.account_id;
                    const row = accountOverrides[aid] || {};
                    const fld = (key) => (
                      <input
                        type="number"
                        step="any"
                        className="h-7 w-24 px-1.5 border border-slate-200 rounded-sm font-mono text-[11.5px] focus:border-indigo-400 focus:outline-none"
                        value={row[key] ?? ""}
                        onChange={(e) => setAcctOverrideField(aid, key, e.target.value)}
                        onBlur={() => persistAcctOverride(aid)}
                        placeholder="—"
                        data-testid={`acct-override-${aid}-${key}`}
                      />
                    );
                    return (
                      <tr key={aid} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`acct-override-row-${aid}`}>
                        <td className="py-1.5 px-2 sticky left-0 bg-white font-bold text-slate-800">{aid}<div className="text-[10px] text-slate-400 font-normal">{a.marketplace || "—"} · {a.account_type || "—"}</div></td>
                        <td className="py-1.5 px-2">{fld("rmb_conversion_rate")}</td>
                        <td className="py-1.5 px-2">{fld("international_freight_electrical")}</td>
                        <td className="py-1.5 px-2">{fld("international_freight_non_electrical")}</td>
                        <td className="py-1.5 px-2">{fld("aggregator_commission_percentage")}</td>
                        <td className="py-1.5 px-2">{fld("importrend_commission_percentage")}</td>
                        <td className="py-1.5 px-2">{fld("gst_percent")}</td>
                        <td className="py-1.5 px-2">
                          <button onClick={() => openFormulaEditor(aid)} data-testid={`acct-formulas-${aid}`}
                                  className="h-6 px-2 mr-1 text-[10px] font-semibold rounded-sm border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100">Formulas</button>
                          <button onClick={() => openRulesEditor(aid)} data-testid={`acct-rules-${aid}`}
                                  className="h-6 px-2 mr-1 text-[10px] font-semibold rounded-sm border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100">Rules</button>
                          <button onClick={() => clearAcctOverride(aid)} data-testid={`acct-override-clear-${aid}`}
                                  className="h-6 px-2 text-[10px] font-semibold rounded-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100">Clear</button>
                        </td>
                      </tr>
                    );
                  })}
                  {!accounts.length && (
                    <tr><td colSpan={8} className="py-8 text-center text-slate-400">No accounts configured yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "calculations" && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="font-heading text-[13px] font-semibold">Calculations Registry</h3>
              <Button variant="primary" onClick={saveGlobals} testId="calc-save-btn">
                <Save size={13} /> Save Constants
              </Button>
            </div>
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-[11.5px] text-amber-900 space-y-0.5">
              <div><b>Forward-only:</b> changing a constant here will only affect <b>new</b> orders (and orders you explicitly hit <i>Recompute</i> on). Every existing order stores the constants it was created with — historical calculations remain frozen.</div>
              <div><b>Formulas are live for arithmetic rows:</b> edits to any row marked <span className="inline-block h-3.5 px-1 bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider rounded-sm align-middle">live</span> immediately drive the actual computation for new orders + any order you hit <i>Recompute</i> on. Invalid formulas (typos, unknown variables, function calls) are rejected on save. Documentation-only rows (Last Mile brackets, aggregate metrics) still edit the reference text only.</div>
            </div>
            <table className="erp-table">
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Metric</th>
                  <th>Formula</th>
                  <th style={{ width: 70 }}>Unit</th>
                  <th style={{ width: 320 }}>Editable Constants</th>
                  <th style={{ width: 90 }}></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Group by `section` (falls back to "General") preserving
                  // original array order so the shipped CALCULATIONS list can
                  // control the intra-section order deterministically.
                  const grouped = [];
                  const seen = new Map();
                  for (const c of calculations) {
                    const sec = c.section || "General";
                    if (!seen.has(sec)) { seen.set(sec, grouped.length); grouped.push({ section: sec, items: [] }); }
                    grouped[seen.get(sec)].items.push(c);
                  }
                  const rows = [];
                  for (const { section, items } of grouped) {
                    rows.push(
                      <tr key={`section-${section}`} className="bg-slate-100">
                        <td colSpan={5} className="px-3 py-1.5 text-[11px] font-heading font-bold uppercase tracking-wider text-slate-700 border-y border-slate-200" data-testid={`calc-section-${section.replace(/\s+/g, '-').toLowerCase()}`}>
                          {section}
                        </td>
                      </tr>
                    );
                    for (const c of items) rows.push(
                  <tr key={c.key} className={c.is_overridden ? "bg-amber-50/40" : undefined}>
                    <td className="align-top">
                      <input
                        type="text"
                        data-testid={`calc-label-${c.key}`}
                        value={c.label ?? ""}
                        onChange={(e) => setCalcOverride(c.key, { label: e.target.value })}
                        onBlur={(e) => persistCalcOverride(c.key, { label: e.target.value === c.default_label ? "" : e.target.value })}
                        className="w-full h-7 px-1.5 text-[13px] font-medium bg-transparent border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none rounded-sm"
                      />
                      <div className="text-[10.5px] text-slate-400 font-mono pl-1.5 flex items-center gap-1">
                        {c.key}
                        {c.live_evaluable && (
                          <span
                            className="inline-block h-4 px-1 bg-emerald-500 text-white text-[9.5px] font-bold uppercase tracking-wider rounded-sm"
                            data-testid={`calc-live-${c.key}`}
                            title="This formula is evaluated live on new orders + Recompute — edits take effect immediately."
                          >live</span>
                        )}
                        {c.is_overridden && (
                          <span className="inline-block h-4 px-1 bg-amber-500 text-white text-[9.5px] font-bold uppercase tracking-wider rounded-sm" data-testid={`calc-overridden-${c.key}`}>overridden</span>
                        )}
                      </div>
                    </td>
                    <td className="align-top">
                      <textarea
                        rows={2}
                        data-testid={`calc-formula-${c.key}`}
                        value={c.formula ?? ""}
                        onChange={(e) => setCalcOverride(c.key, { formula: e.target.value })}
                        onBlur={(e) => persistCalcOverride(c.key, { formula: e.target.value === c.default_formula ? "" : e.target.value })}
                        className="w-full text-[12px] text-slate-700 font-mono px-1.5 py-1 border border-transparent hover:border-slate-200 focus:border-blue-500 focus:outline-none rounded-sm resize-y"
                      />
                    </td>
                    <td className="font-mono text-[11px] align-top">{c.unit}</td>
                    <td className="align-top">
                      {c.editable_constants.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {c.editable_constants.map((k) => (
                            <label key={k} className="inline-flex items-center gap-1 text-[10.5px] font-mono bg-blue-50 border border-blue-200 text-blue-800 px-1.5 py-0.5 rounded-sm">
                              <span>{k}</span>
                              <input
                                type="number"
                                step="0.01"
                                data-testid={`calc-const-${k}`}
                                value={globals[k] ?? ""}
                                onChange={(e) => setGlobals((s) => ({ ...s, [k]: Number(e.target.value) }))}
                                className="w-20 h-6 px-1 text-[11px] font-mono bg-white border border-blue-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                            </label>
                          ))}
                        </div>
                      ) : <span className="text-slate-400 text-[11px]">—</span>}
                    </td>
                    <td className="align-top">
                      {c.is_overridden && (
                        <button
                          onClick={() => resetCalcOverride(c.key)}
                          className="text-[10.5px] font-mono text-red-700 hover:underline"
                          data-testid={`calc-reset-${c.key}`}
                        >Reset default</button>
                      )}
                    </td>
                  </tr>
                    );
                  }
                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        )}

        {tab === "dropdowns" && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-[13px] font-semibold">Dropdown Values</h3>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
              {Object.entries(dropdowns).map(([key, values]) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="field-label !mb-0">{key.replaceAll("_", " ")}</span>
                    <div className="flex gap-1.5">
                      <Input testId={T.formInput(`dd-${key}`)} value={newDD[key] || ""} onChange={(e) => setNewDD((s) => ({ ...s, [key]: e.target.value }))} className="h-7 w-40" placeholder="Add value…" />
                      <Button className="h-7" onClick={() => addDD(key)}><Plus size={12} /></Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {values.map((v) => (
                      <span key={v} className="inline-flex items-center gap-1 h-6 px-2 text-[11.5px] bg-slate-100 border border-slate-200 rounded-sm">
                        {v}
                        <button onClick={() => removeDD(key, v)} className="text-slate-400 hover:text-red-700"><Trash2 size={11} /></button>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "custom" && (
          <div className="bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-[13px] font-semibold">Custom Fields per Module</h3>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {MODULES.map((m) => (
                <div key={m}>
                  <div className="field-label capitalize">{m}</div>
                  <div className="flex gap-1.5 mb-2">
                    <Input testId={T.formInput(`cf-${m}`)} value={newCF[m] || ""} onChange={(e) => setNewCF((s) => ({ ...s, [m]: e.target.value }))} placeholder="Field name" className="h-7" />
                    <Button className="h-7" onClick={() => addCF(m)}><Plus size={12} /></Button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(customFields[m] || []).map((f) => (
                      <span key={f.name} className="inline-flex items-center gap-1 h-6 px-2 text-[11.5px] bg-slate-100 border border-slate-200 rounded-sm">
                        {f.name}
                        <button onClick={() => removeCF(m, f.name)} className="text-slate-400 hover:text-red-700"><Trash2 size={11} /></button>
                      </span>
                    ))}
                    {!(customFields[m] || []).length && <span className="text-[11.5px] text-slate-400">No custom fields</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "label" && (
          <div className="max-w-3xl bg-white border border-slate-200 rounded-sm" data-testid="settings-label-panel">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div>
                <h3 className="font-heading text-[13px] font-semibold">Shipping Label Template</h3>
                <div className="text-[10.5px] text-slate-500">
                  Shiprocket-style 100×150mm layout. Sender name/address/GSTIN/disclaimer are baked into every PDF you generate.
                </div>
              </div>
              <Button variant="primary" onClick={saveLabelTemplate} disabled={labelBusy || !labelTpl} testId="label-tpl-save">
                <Save size={13} /> {labelBusy ? "Saving…" : "Save"}
              </Button>
            </div>
            {labelTpl ? (
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Company Logo URL (top-right)">
                    <Input testId="label-tpl-logo-url" value={labelTpl.logo_url || ""}
                           onChange={(e) => setLabelTpl((s) => ({ ...s, logo_url: e.target.value }))}
                           placeholder="https://cdn.example.com/logo.png" />
                  </Field>
                  <Field label="Powered By footer text">
                    <Input testId="label-tpl-powered-by" value={labelTpl.powered_by || ""}
                           onChange={(e) => setLabelTpl((s) => ({ ...s, powered_by: e.target.value }))}
                           placeholder="Powered By: Importrend" />
                  </Field>
                  <Field label="Shipped By — Sender Name (override; blank = uses account_id)">
                    <Input testId="label-tpl-shipped-by-name" value={labelTpl.shipped_by_name || ""}
                           onChange={(e) => setLabelTpl((s) => ({ ...s, shipped_by_name: e.target.value }))}
                           placeholder="Leave blank to print the order's account_id" />
                  </Field>
                  <Field label="Shipped By — Return Address (override; blank = uses warehouse mapped to account)">
                    <Input testId="label-tpl-shipped-by-address" value={labelTpl.shipped_by_address || ""}
                           onChange={(e) => setLabelTpl((s) => ({ ...s, shipped_by_address: e.target.value }))}
                           placeholder="Blank = auto-resolves the account's warehouse address" />
                  </Field>
                  <Field label="Jurisdiction (used in disclaimer)">
                    <Input testId="label-tpl-jurisdiction" value={labelTpl.jurisdiction || ""}
                           onChange={(e) => setLabelTpl((s) => ({ ...s, jurisdiction: e.target.value }))}
                           placeholder="Uttar Pradesh" />
                  </Field>
                  <Field label="Disclaimer (footer text)">
                    <Input testId="label-tpl-disclaimer" value={labelTpl.disclaimer || ""}
                           onChange={(e) => setLabelTpl((s) => ({ ...s, disclaimer: e.target.value }))}
                           placeholder="All disputes are subject to {jurisdiction} jurisdiction only…" />
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
                  {[
                    ["show_logo", "Show Logo"],
                    ["show_awb_barcode", "AWB Barcode"],
                    ["show_order_id_barcode", "Order ID Barcode"],
                  ].map(([key, label]) => (
                    <label
                      key={key}
                      data-testid={`label-tpl-${key}`}
                      className="inline-flex items-center gap-2 px-2 py-1.5 border border-slate-200 rounded-sm bg-white hover:bg-slate-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={!!labelTpl[key]}
                        onChange={(e) => setLabelTpl((s) => ({ ...s, [key]: e.target.checked }))}
                        className="accent-slate-800"
                      />
                      <span className="text-[12px] text-slate-800">{label}</span>
                    </label>
                  ))}
                </div>
                <div className="text-[10.5px] text-slate-500 pt-2 border-t border-slate-200">
                  Layout: Ship To + Courier/AWB barcode · Shipped By (auto-fills the account_id + mapped warehouse address) + Order # barcode · Product / SKU / QTY · disclaimer footer.
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-slate-500 text-[12px]">Loading template…</div>
            )}
          </div>
        )}

        {tab === "gmail" && (
          <div className="max-w-2xl bg-white border border-slate-200 rounded-sm">
            <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <h3 className="font-heading text-[13px] font-semibold">Gmail Reader (IMAP)</h3>
              <span className="text-[10.5px] text-slate-500">Reads Amazon payout / commission / refund emails — 100% free (Python stdlib, no external API)</span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="field-label">IMAP Host</div>
                <Input testId="gmail-host" value={gmailCfg.imap_host || ""}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, imap_host: e.target.value }))}
                       placeholder="imap.gmail.com" />
              </div>
              <div>
                <div className="field-label">Port</div>
                <Input testId="gmail-port" type="number" value={gmailCfg.imap_port || 993}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, imap_port: Number(e.target.value) }))} />
              </div>
              <div className="md:col-span-2">
                <div className="field-label">Gmail Address</div>
                <Input testId="gmail-email" type="email" value={gmailCfg.email || ""}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, email: e.target.value }))}
                       placeholder="ops@yourbrand.com" />
              </div>
              <div className="md:col-span-2">
                <div className="field-label">App Password (16-char, generated at https://myaccount.google.com/apppasswords)</div>
                <Input testId="gmail-password" type="password"
                       value={gmailCfg.app_password || ""}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, app_password: e.target.value }))}
                       placeholder="16-character app password" />
                <div className="text-[10.5px] text-slate-500 mt-1">Stored server-side. Two-factor auth must be ON for the Gmail account to generate app passwords.</div>
              </div>
              <div>
                <div className="field-label">Folder</div>
                <Input testId="gmail-folder" value={gmailCfg.folder || "INBOX"}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, folder: e.target.value }))} />
              </div>
              <div>
                <div className="field-label">Search window (days) — higher = deeper history</div>
                <Input testId="gmail-days" type="number" min="1" max="365"
                       value={gmailCfg.search_days ?? 90}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, search_days: Number(e.target.value) }))} />
              </div>
              <div className="md:col-span-2">
                <div className="field-label">Max emails per run — bump higher if 400+ orders still show 0 payout</div>
                <Input testId="gmail-max-per-run" type="number" min="50" max="5000" step="50"
                       value={gmailCfg.max_per_run ?? 500}
                       onChange={(e) => setGmailCfg((s) => ({ ...s, max_per_run: Number(e.target.value) }))} />
                <div className="text-[10.5px] text-slate-500 mt-1">Default 500. Increase to 2000+ if the settlement email you're looking for is older than 500 messages back in the inbox.</div>
              </div>
              <div className="md:col-span-2 flex items-center gap-2 pt-2 border-t border-slate-100 mt-2">
                <Button variant="primary" onClick={saveGmail} testId="gmail-save">
                  <Save size={13} /> Save Settings
                </Button>
                <Button onClick={syncGmail} disabled={gmailBusy} testId="gmail-sync-now"
                        className="!bg-emerald-600 hover:!bg-emerald-700 !text-white !border-emerald-600 disabled:opacity-60">
                  {gmailBusy
                    ? <><RefreshCw size={13} className="animate-spin" /> Syncing…</>
                    : <><Mail size={13} /> Sync From Gmail Now</>}
                </Button>
                <Button onClick={reapplyCached} disabled={gmailBusy} testId="gmail-reapply-cached"
                        className="!bg-indigo-600 hover:!bg-indigo-700 !text-white !border-indigo-600 disabled:opacity-60">
                  <RefreshCw size={13} /> Re-apply Cached
                </Button>
                <Button onClick={rescanUnclassified} disabled={gmailBusy} testId="gmail-rescan-unclassified"
                        className="!bg-amber-600 hover:!bg-amber-700 !text-white !border-amber-600 disabled:opacity-60">
                  <RefreshCw size={13} /> Rescan Skipped
                </Button>
                <Button onClick={repairRefundStatuses} disabled={gmailBusy} testId="gmail-repair-refund-statuses"
                        className="!bg-rose-600 hover:!bg-rose-700 !text-white !border-rose-600 disabled:opacity-60">
                  <RefreshCw size={13} /> Fix Refunded Orders
                </Button>
                <Button onClick={analyzeOverlap} disabled={gmailBusy} testId="gmail-analyze"
                        className="!bg-slate-700 hover:!bg-slate-800 !text-white !border-slate-700 disabled:opacity-60">
                  <Mail size={13} /> Analyze Overlap
                </Button>
                <span className="ml-auto text-[10.5px] text-slate-500">Background sync runs every 15 min automatically</span>
              </div>
              {gmailAnalysis && (
                <div className="md:col-span-2 bg-slate-50 border border-slate-300 rounded-sm px-3 py-3" data-testid="gmail-analysis">
                  <div className="text-[12px] font-semibold text-slate-900 mb-2">Overlap Analysis — Gmail events vs Orders in DB</div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-[11.5px] font-mono mb-3">
                    <div><span className="text-slate-600">Orders total:</span> <span className="font-semibold">{gmailAnalysis.orders_total}</span></div>
                    <div><span className="text-slate-600">Amazon orders:</span> <span className="font-semibold">{gmailAnalysis.orders_amazon}</span></div>
                    <div><span className="text-slate-600">With payout:</span> <span className="font-semibold text-emerald-700">{gmailAnalysis.orders_with_payout}</span></div>
                    <div><span className="text-slate-600">With commission:</span> <span className="font-semibold text-emerald-700">{gmailAnalysis.orders_with_commission}</span></div>
                    <div><span className="text-slate-600">Refunded:</span> <span className="font-semibold text-emerald-700">{gmailAnalysis.orders_refunded}</span></div>
                  </div>
                  {["payout", "refund"].map((kind) => {
                    const k = gmailAnalysis.by_kind[kind] || {};
                    const missing = k.sample_missing_from_db || [];
                    const matched = k.sample_matched || [];
                    return (
                      <div key={kind} className="mb-3 pt-2 border-t border-slate-200">
                        <div className="text-[11.5px] font-semibold text-slate-800 mb-1 capitalize">
                          {kind} events: <span className="font-mono">{k.events_total}</span> total ·
                          <span className="text-emerald-700"> {k.order_ids_in_db} matched</span> ·
                          <span className="text-amber-700"> {missing.length}+ missing from DB</span>
                        </div>
                        {missing.length > 0 && (
                          <div className="text-[10.5px]">
                            <span className="text-amber-800 font-semibold">Missing order-IDs</span> — import these to auto-apply:
                            <div className="font-mono text-[10px] text-slate-700 bg-white border border-slate-200 rounded-sm px-2 py-1 mt-1">
                              {missing.join(", ")}
                            </div>
                          </div>
                        )}
                        {matched.length > 0 && (
                          <div className="text-[10.5px] mt-1 text-slate-500">
                            <span className="text-emerald-700 font-semibold">Matched (sample):</span> <span className="font-mono text-[10px]">{matched.slice(0, 5).join(", ")}{matched.length > 5 ? ` +${matched.length - 5} more` : ""}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {gmailStatus?.status === "running" && typeof gmailStatus?.total_uids === "number" && (
                <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-sm px-3 py-2" data-testid="gmail-progress">
                  <div className="flex justify-between text-[11px] font-mono text-blue-900 mb-1">
                    <span>Processing {gmailStatus.processed ?? 0} / {gmailStatus.total_uids}</span>
                    <span>{gmailStatus.total_uids ? Math.round(((gmailStatus.processed ?? 0) / gmailStatus.total_uids) * 100) : 0}%</span>
                  </div>
                  <div className="h-1.5 bg-blue-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all"
                         style={{ width: `${gmailStatus.total_uids ? ((gmailStatus.processed ?? 0) / gmailStatus.total_uids) * 100 : 0}%` }} />
                  </div>
                </div>
              )}
              {gmailStatus?.status === "failed" && (
                <div className="md:col-span-2 bg-red-50 border border-red-200 rounded-sm px-3 py-2 text-[11.5px] text-red-800" data-testid="gmail-error">
                  Last sync failed: {gmailStatus.error || "unknown error"}
                </div>
              )}
              {gmailStatus?.ran_at && (
                <div className="md:col-span-2 bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2" data-testid="gmail-summary">
                  <div className="text-[11px] text-emerald-900 font-semibold mb-1">
                    Last sync {new Date(gmailStatus.ran_at).toLocaleString()}
                    {gmailStatus.ran_by && <span className="ml-2 text-emerald-700 font-normal">by {gmailStatus.ran_by}</span>}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11.5px] font-mono">
                    <div><span className="text-slate-600">New emails parsed:</span> <span className="font-semibold">{gmailStatus.new_events_parsed ?? 0}</span></div>
                    <div><span className="text-slate-600">Re-applied cached:</span> <span className="font-semibold">{gmailStatus.reapplied_events ?? 0}</span></div>
                    <div><span className="text-slate-600">Unmatched (pending):</span> <span className="font-semibold text-amber-700">{gmailStatus.unapplied_events ?? 0}</span></div>
                    <div><span className="text-slate-600">Server matches:</span> <span className="font-semibold text-blue-700">{gmailStatus.server_side_matches ?? 0}</span></div>
                  </div>
                  {gmailStatus.remaining_to_scan > 0 && (
                    <div className="mt-2 bg-blue-50 border border-blue-200 rounded-sm px-2 py-1.5 text-[11px] text-blue-900">
                      <span className="font-semibold">Incremental catch-up:</span> {gmailStatus.processed_uids_total ?? 0} / {gmailStatus.server_side_matches ?? 0} emails processed
                      · <span className="font-semibold">{gmailStatus.remaining_to_scan} remaining</span>
                      · auto-sync every 15 min will keep chewing through them
                      <div className="h-1 bg-blue-100 rounded-sm overflow-hidden mt-1">
                        <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, ((gmailStatus.processed_uids_total ?? 0) / (gmailStatus.server_side_matches || 1)) * 100)}%` }} />
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11.5px] font-mono mt-1.5 pt-1.5 border-t border-emerald-200">
                    <div><span className="text-slate-600">Refunds:</span> <span className="font-semibold text-emerald-800">{gmailStatus.refunds_applied ?? 0}</span></div>
                    <div><span className="text-slate-600">Payouts:</span> <span className="font-semibold text-emerald-800">{gmailStatus.payouts_applied ?? 0}</span></div>
                    <div><span className="text-slate-600">Commissions:</span> <span className="font-semibold text-emerald-800">{gmailStatus.commissions_applied ?? 0}</span></div>
                    <div><span className="text-slate-600">Delivery:</span> <span className="font-semibold text-emerald-800">{gmailStatus.delivery_applied ?? 0}</span></div>
                  </div>
                  {(gmailStatus.unapplied_events ?? 0) > 0 && (
                    <div className="mt-2 text-[10.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
                      <span className="font-semibold">{gmailStatus.unapplied_events} email(s) parsed but not yet applied</span> — the order isn't in the DB yet. Import the missing orders, then click <span className="font-semibold">Re-apply Cached</span> above (or wait for the next auto-sync).
                    </div>
                  )}
                  {(gmailStatus.unmatched_order_ids || []).length > 0 && (
                    <div className="mt-2 text-[10.5px] text-amber-800">
                      <span className="font-semibold">Unmatched order IDs ({gmailStatus.unmatched_order_ids.length}):</span>{" "}
                      <span className="font-mono">{gmailStatus.unmatched_order_ids.slice(0, 10).join(", ")}
                        {gmailStatus.unmatched_order_ids.length > 10 && ` +${gmailStatus.unmatched_order_ids.length - 10} more`}</span>
                    </div>
                  )}
                  {(gmailStatus.skipped_samples || []).length > 0 && (
                    <div className="mt-2" data-testid="gmail-skipped-samples">
                      <div className="text-[10.5px] font-semibold text-slate-700 mb-1">
                        Recent skipped subjects ({gmailStatus.skipped_samples.length}) — if a real settlement/refund is here, tell me the exact wording so I can add the pattern
                      </div>
                      <div className="max-h-40 overflow-y-auto bg-white border border-slate-200 rounded-sm p-2 space-y-0.5 font-mono text-[10.5px] text-slate-700">
                        {gmailStatus.skipped_samples.map((s, i) => <div key={i} className="truncate" title={s}>· {s}</div>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </PageBody>

      {formulaEditor && (
        <div className="fixed inset-0 bg-slate-950/40 z-50 flex items-center justify-center p-4"
             onClick={() => setFormulaEditor(null)} data-testid="acct-formula-modal">
          <div className="bg-white rounded-sm shadow-2xl border border-slate-200 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 bg-slate-50">
              <div>
                <div className="text-[13px] font-bold uppercase tracking-wider text-slate-800">Per-Account Formulas</div>
                <div className="font-mono text-[11px] text-slate-500">Account · <span className="text-slate-800 font-bold">{formulaEditor.account_id}</span></div>
              </div>
              <button onClick={() => setFormulaEditor(null)} className="text-slate-400 hover:text-slate-700 text-lg">×</button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-2 text-[11px] text-indigo-900 font-mono">
                Type any expression using these variables: <code>china_price</code>, <code>china_shipping</code>, <code>moq</code>,{" "}
                <code>china_weight_gram</code>, <code>rmb_conversion_rate</code>, <code>international_freight_rate</code>,{" "}
                <code>gst_rate</code>, <code>aggregator_commission_percentage</code>, <code>importrend_commission_percentage</code>,{" "}
                <code>china_costing</code>, <code>china_intl_freight</code>, <code>china_agg_margin</code>, <code>china_gst</code>,{" "}
                <code>landing_price</code>, <code>importrend_margin</code>, <code>importrend_margin_gst</code>, <code>transfer_price</code>.
                Leave blank to fall back to the shipped default.
              </div>
              {Object.entries(formulaEditor.defaults || {}).map(([key, def]) => {
                const override = formulaEditor.overrides?.[key] ?? "";
                const isOverridden = override && override !== def;
                return (
                  <div key={key} className="border border-slate-200 rounded-sm p-3 bg-white" data-testid={`acct-formula-row-${key}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="font-heading text-[12px] font-semibold text-slate-800">{key}</div>
                      {isOverridden && (
                        <span className="text-[10px] font-bold uppercase text-indigo-700 bg-indigo-100 border border-indigo-300 rounded-sm px-1.5 py-0.5">Overridden</span>
                      )}
                    </div>
                    <div className="text-[10.5px] font-mono text-slate-500 mb-1.5">
                      Default: <span className="text-slate-700">{def}</span>
                    </div>
                    <textarea
                      className="w-full min-h-[52px] px-2 py-1.5 border border-slate-200 rounded-sm font-mono text-[11.5px] focus:border-indigo-400 focus:outline-none"
                      placeholder="Leave blank to use the default"
                      value={override}
                      onChange={(e) => setFormulaOverride(key, e.target.value)}
                      onBlur={() => persistFormulaOverride(key)}
                      data-testid={`acct-formula-input-${key}`}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <button onClick={() => setFormulaEditor(null)}
                      className="h-8 px-3 text-[12px] font-semibold rounded-sm border border-slate-300 bg-white hover:bg-slate-50"
                      data-testid="acct-formula-close">Close</button>
            </div>
          </div>
        </div>
      )}

      {rulesEditor && (
        <div className="fixed inset-0 bg-slate-950/40 z-50 flex items-center justify-center p-4"
             onClick={() => setRulesEditor(null)} data-testid="acct-rules-modal">
          <div className="bg-white rounded-sm shadow-2xl border border-slate-200 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 bg-slate-50">
              <div>
                <div className="text-[13px] font-bold uppercase tracking-wider text-slate-800">Standalone Status Rules</div>
                <div className="font-mono text-[11px] text-slate-500">Account · <span className="text-slate-800 font-bold">{rulesEditor.account_id}</span></div>
              </div>
              <button onClick={() => setRulesEditor(null)} className="text-slate-400 hover:text-slate-700 text-lg">×</button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2 text-[11px] text-emerald-900">
                Rules run <b>top-down; first match wins</b>. When no rule matches (or no rules are configured), new standalone procurement entries for this account default to <code className="font-mono">Blank</code>.
                Only applies to <b>Direct-Source (standalone)</b> procurement — order-linked entries keep their existing status logic.
              </div>
              <table className="w-full text-[11.5px]" data-testid="rules-editor-table">
                <thead>
                  <tr className="text-left text-slate-500 uppercase tracking-wider border-b border-slate-200 bg-slate-50 text-[10px]">
                    <th className="py-1.5 px-2 w-8">#</th>
                    <th className="py-1.5 px-2">Field</th>
                    <th className="py-1.5 px-2 w-20">Operator</th>
                    <th className="py-1.5 px-2 w-32">Value</th>
                    <th className="py-1.5 px-2">Result Status</th>
                    <th className="py-1.5 px-2 w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {rulesEditor.rules.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-100" data-testid={`rules-row-${idx}`}>
                      <td className="py-1.5 px-2 font-mono text-slate-500">{idx + 1}</td>
                      <td className="py-1.5 px-2">
                        <input list="rule-field-choices" value={r.field}
                               onChange={(e) => updateRuleAt(idx, { field: e.target.value })}
                               className="h-7 w-full px-1.5 border border-slate-200 rounded-sm font-mono text-[11.5px] focus:border-emerald-400 focus:outline-none"
                               data-testid={`rules-field-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <select value={r.operator}
                                onChange={(e) => updateRuleAt(idx, { operator: e.target.value })}
                                className="h-7 w-full px-1 border border-slate-200 rounded-sm font-mono text-[11.5px] focus:border-emerald-400 focus:outline-none"
                                data-testid={`rules-op-${idx}`}>
                          {RULE_OPERATORS.map((op) => <option key={op} value={op}>{op}</option>)}
                        </select>
                      </td>
                      <td className="py-1.5 px-2">
                        <input value={r.value ?? ""}
                               onChange={(e) => updateRuleAt(idx, { value: e.target.value })}
                               className="h-7 w-full px-1.5 border border-slate-200 rounded-sm font-mono text-[11.5px] focus:border-emerald-400 focus:outline-none"
                               placeholder="1"
                               data-testid={`rules-value-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <input list="rule-status-choices" value={r.result_status}
                               onChange={(e) => updateRuleAt(idx, { result_status: e.target.value })}
                               className="h-7 w-full px-1.5 border border-slate-200 rounded-sm font-mono text-[11.5px] focus:border-emerald-400 focus:outline-none"
                               data-testid={`rules-status-${idx}`} />
                      </td>
                      <td className="py-1.5 px-2">
                        <button onClick={() => moveRule(idx, -1)} disabled={idx === 0}
                                className="h-6 w-6 mr-0.5 text-[10px] font-bold rounded-sm border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40"
                                data-testid={`rules-up-${idx}`} title="Move up">↑</button>
                        <button onClick={() => moveRule(idx, 1)} disabled={idx === rulesEditor.rules.length - 1}
                                className="h-6 w-6 mr-0.5 text-[10px] font-bold rounded-sm border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40"
                                data-testid={`rules-down-${idx}`} title="Move down">↓</button>
                        <button onClick={() => removeRuleAt(idx)}
                                className="h-6 w-6 text-[10px] font-bold rounded-sm border border-red-200 bg-red-50 text-red-700 hover:bg-red-100"
                                data-testid={`rules-remove-${idx}`} title="Remove">×</button>
                      </td>
                    </tr>
                  ))}
                  {!rulesEditor.rules.length && (
                    <tr><td colSpan={6} className="py-6 text-center text-slate-400">No rules yet. Click <b>+ Add Rule</b> to create one.</td></tr>
                  )}
                </tbody>
              </table>
              <datalist id="rule-field-choices">
                {RULE_FIELD_CHOICES.map((f) => <option key={f} value={f} />)}
              </datalist>
              <datalist id="rule-status-choices">
                {RULE_STATUS_CHOICES.map((s) => <option key={s} value={s} />)}
              </datalist>
              <button onClick={addRule}
                      className="h-8 px-3 text-[12px] font-semibold rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 inline-flex items-center gap-1"
                      data-testid="rules-add-btn">
                <Plus size={13} /> Add Rule
              </button>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <button onClick={() => setRulesEditor(null)}
                      className="h-8 px-3 text-[12px] font-semibold rounded-sm border border-slate-300 bg-white hover:bg-slate-50"
                      data-testid="acct-rules-close">Close</button>
              <button onClick={persistRules}
                      className="h-8 px-3 text-[12px] font-semibold rounded-sm border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                      data-testid="acct-rules-save">
                <Save size={13} /> Save Rules
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
